import React from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Link } from 'react-router-dom';
import {
  Backdrop,
  ButtonIcon,
  IconX,
  Image,
  IconUserAltCircle,
  Type,
  IconChevronDown,
  IconChevronUp,
  IconArrowRight,
  IconGears,
  IconHelpAlt,
  IconPlusAlt,
} from '@spotify-internal/encore-creator-web';
import {
  white,
  gray10,
  screenXsMin,
  gray60,
  spacer20,
  body2FontSize,
  spacer16,
  gray20,
  spacer12,
  black,
} from '@spotify-internal/encore-foundation';
import { SpotifyForPodcastersLogo } from '../../../vendor/SpotifyForPodcastersLogo';
import { trackEvent } from '../../../lib/Tracking';
import { useMe } from '../../../hooks/useMe';
import { useIntl, defineMessage, FormattedMessage } from 'react-intl';
import { PODCASTERS_ZENDESK_LINK } from '../../../lib/constants';
import { useLocalePreferenceContext } from '../../../hooks/useLocalePreference';
import { auth } from '../../../lib/Request';

type Props = {
  toggleUserFlyout: () => void;
  toggleMobileNavigation: () => void;
  navLinks: React.ReactNode;
  userFlyoutOpen: boolean;
  navFlyoutOpen: boolean;
};

export function Flyout(props: Props) {
  const me = useMe()!;
  const [userNavOpen, setUserNavOpen] = React.useState(false);
  const intl = useIntl();
  const localePreference = useLocalePreferenceContext();
  const closeNavigationText = defineMessage({
    id: 'navigation.close',
    desciption: 'Text telling user to hide side navigation bar',
    defaultMessage: 'Close Navigation',
  });

  return (
    <React.Fragment>
      {props.userFlyoutOpen && (
        <React.Fragment>
          <PreventScrolling />
          <AnimatedBackdrop data-testid="mobile-nav-backdrop" onClose={props.toggleUserFlyout} />
        </React.Fragment>
      )}
      {props.navFlyoutOpen && (
        <React.Fragment>
          <PreventScrolling />
          <AnimatedBackdrop
            data-testid="mobile-nav-backdrop"
            onClose={props.toggleMobileNavigation}
          />
        </React.Fragment>
      )}
      <FlyoutContainer
        data-testid="user-flyout"
        position="left"
        status={props.userFlyoutOpen ? 'opened' : 'closed'}
      >
        <div style={{ paddingLeft: spacer20, paddingRight: spacer20 }}>
          <UserHeader>
            <LogoContainer>
              <SpotifyForPodcastersLogo />
            </LogoContainer>
            <ButtonIcon
              data-testid="user-sub-nav"
              onClick={props.toggleUserFlyout}
              aria-label={intl.formatMessage(closeNavigationText)}
            >
              <IconX color={white} />
            </ButtonIcon>
          </UserHeader>
          <div>
            <SubmitLink to="/submit" onClick={props.toggleUserFlyout}>
              <IconPlusAlt style={{ marginRight: spacer12 }} />
              <FormattedMessage
                id="navigation.add-podcast"
                description="Link for a user to add or claim their podcast"
                defaultMessage="Add or claim your podcast"
              />
            </SubmitLink>
          </div>
        </div>
        <UserFooter status={userNavOpen ? 'opened' : 'closed'} data-testid="nav-user-links">
          <AccordionToggle onClick={() => setUserNavOpen(open => !open)}>
            {Boolean(me.avatarUrl) ? (
              <Image
                src={me.avatarUrl}
                imageHeight="36px"
                imageWidth="36px"
                alt={me.displayName}
                crop
                circle
                data-testid="nav-avatar-footer"
              />
            ) : (
              <IconUserAltCircle
                iconSize={36}
                aria-label={me.displayName}
                data-testid="nav-avatar-footer-fallback"
              />
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                marginLeft: '12px',
              }}
            >
              <Type as="p" color={white} style={{ padding: 0 }}>
                {me.displayName}
              </Type>
              <Type as="p" style={{ padding: 0 }}>
                {me.emailAddress}
              </Type>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              {userNavOpen ? (
                <IconChevronDown color={white} iconSize={16} />
              ) : (
                <IconChevronUp color={white} iconSize={16} />
              )}
            </div>
          </AccordionToggle>
          <nav data-testid="user-nav-menu-items">
            <ul style={{ padding: 0 }}>
              <BorderAfter>
                <InternalNavLink to="/settings" onClick={props.toggleUserFlyout}>
                  <IconGears style={{ marginRight: '12px' }} />
                  <FormattedMessage
                    id="navigation.user-settings"
                    description="Link for a user to go to thier settings page"
                    defaultMessage="User settings"
                  />
                </InternalNavLink>
              </BorderAfter>
              <BorderAfter>
                <ExternalNavLink
                  href={PODCASTERS_ZENDESK_LINK + localePreference.languageAndRegionCode}
                  onClick={props.toggleUserFlyout}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconHelpAlt style={{ marginRight: '12px' }} />{' '}
                  <FormattedMessage
                    id="navigation.faq"
                    description="Link for a user to go to the frequently asked questions page"
                    defaultMessage="FAQ"
                  />
                </ExternalNavLink>
              </BorderAfter>
              <li>
                <ExternalNavButton
                  onClick={() => {
                    trackEvent({ category: 'user', action: 'logout' });
                    auth.logout(window.location.origin);
                  }}
                >
                  <IconArrowRight style={{ marginRight: '12px' }} />{' '}
                  <FormattedMessage
                    id="navigation.logout"
                    description="Link for a user to log out and close thier session"
                    defaultMessage="Log out"
                  />
                </ExternalNavButton>
              </li>
            </ul>
          </nav>
        </UserFooter>
      </FlyoutContainer>
      <FlyoutContainer
        status={props.navFlyoutOpen ? 'opened' : 'closed'}
        data-testid="mobile-nav-flyout"
        position="right"
      >
        <MobileHeader>
          <LogoContainer>
            <SpotifyForPodcastersLogo />
          </LogoContainer>
          <ButtonIcon
            data-testid="mobile-nav-close"
            onClick={props.toggleMobileNavigation}
            aria-label={intl.formatMessage(closeNavigationText)}
          >
            <IconX color={white} />
          </ButtonIcon>
        </MobileHeader>
        <MobileNav>{props.navLinks}</MobileNav>
      </FlyoutContainer>
    </React.Fragment>
  );
}

const AnimatedBackdrop = styled(Backdrop)`
  opacity: 1;
`;

const PreventScrolling = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

const LogoContainer = styled.div`
  max-width: 180px;
`;

const SubmitLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${gray60};
  font-size: ${body2FontSize};
  text-decoration: none;
  padding: 22px 6px;

  &:hover {
    color: ${white};
  }
`;

const UserHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray20};
  padding: 25px 0px;
`;

const MobileHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray20};
  padding: ${spacer16};
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 18px;
  padding: ${spacer16};

  > * {
    padding: ${spacer16};
  }
`;

const BorderAfter = styled.li`
  position: relative;

  &:after {
    content: '';
    height: 1px;
    width: calc(100% - 48px);
    background-color: rgb(64, 64, 64);
    position: absolute;
    bottom: 0px;
    left: 24px;
  }
`;

type FlyoutProps = {
  position: 'left' | 'right';
  status: 'opened' | 'closed';
};

const shadowDepth = '14px';

const FlyoutContainer = styled.div<FlyoutProps>`
  position: fixed;
  top: 0;
  ${props => (props.position === 'left' ? 'left: 0;' : 'right: 0;')}
  right: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 ${shadowDepth} 0 ${black};
  height: 100%;
  z-index: 1050;
  width: 100%;
  background: ${gray10};
  transform: translateX(
    ${props => {
      switch (props.position) {
        case 'left': {
          return props.status === 'opened' ? 0 : `calc(-100% - ${shadowDepth})`;
        }
        case 'right': {
          return props.status === 'opened' ? 0 : `calc(100% + ${shadowDepth})`;
        }
      }
    }}
  );
  transition: transform 90ms;
  transition-timing-function: cubic-bezier(0.3, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (min-width: ${screenXsMin}) {
    width: 336px;
  }
`;

const UserFooter = styled.footer<{ status: 'opened' | 'closed' }>`
  display: flex;
  flex-direction: column;
  color: rgb(189, 189, 189);
  background-color: rgb(40, 40, 40);
  margin-top: auto;
  min-height: 80px;
  overflow-y: auto;
  position: relative;
  transform: translateY(
    ${props => (props.status === 'opened' ? '0' : `calc(100% - ${userButtonHeight})`)}
  );
  transition: transform 90ms;
  transition-timing-function: cubic-bezier(0.3, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const userButtonHeight = '80px';

const AccordionToggle = styled.button`
  display: flex;
  align-items: center;
  border: 0;
  padding: 16px 8px;
  background: rgb(51, 51, 51);
  line-height: 1;
  letter-spacing: inherit;
  color: rgb(189, 189, 189);
  width: 100%;
  position: relative;
  height: ${userButtonHeight};
  flex-shrink: 0;
  border-width: 0px;
  padding: 0px 24px;

  &:hover {
    color: rgb(255, 255, 255);
    box-shadow: rgb(51, 51, 51) 0px 1px 0px 0px, rgb(51, 51, 51) 0px -1px 0px 0px;
    background: rgb(51, 51, 51);
    z-index: 1;
  }

  &:focus {
    color: rgb(255, 255, 255);
    box-shadow: rgb(51, 51, 51) 0px 1px 0px 0px, rgb(51, 51, 51) 0px -1px 0px 0px;
    z-index: 1;
    background: rgb(51, 51, 51);
  }
`;

const NavLinkStyles = css`
  color: rgb(189, 189, 189);
  display: flex;
  align-items: center;
  position: relative;
  padding: 20px 24px;

  &:hover,
  &:focus {
    color: rgb(255, 255, 255);
    background-color: rgb(51, 51, 51);
    box-shadow: rgb(51, 51, 51) 0px 1px 0px 0px, rgb(51, 51, 51) 0px -1px 0px 0px;
    z-index: 1;
  }
`;

const InternalNavLink = styled(Link)`
  ${NavLinkStyles}
`;

const ExternalNavLink = styled.a`
  ${NavLinkStyles}
`;

const ExternalNavButton = styled.button`
  width: 100%;
  background: none;
  cursor: pointer;
  border: none;
  ${NavLinkStyles}
`;
